import { asText } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import DynamicForm from '@remote-com/mushu/src/components/Form/DynamicForm';
import {
  SuccessTitle,
  SuccessDescription,
  SuccessActions,
} from '@remote-com/mushu/src/components/Success';
import SuccessMark from '@remote-com/mushu/src/components/Success/SuccessMark';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import { useFormEvents } from '@remote-com/mushu/src/hooks/useFormEvents';
import axios from 'axios';
import getConfig from 'next/config';
import React, { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button, ButtonInline } from 'remote-com-shared/components/Button';
import { dataLayerPush } from 'remote-com-shared/components/CookieScripts/helpers';
import { Modal } from 'remote-com-shared/components/ModalForm';
import { getAuthTypeFromReasonOption } from 'remote-com-shared/components/ModalForm/helpers';
import {
  getCurrentURL,
  registerLead,
  createPartnerStackSignup,
  getPartnerKey,
} from 'remote-com-shared/components/ModalForm/helpers/services';
import {
  FormContainer,
  SubmitButton,
  CaptchaWrapper,
  SubsribeSuccessContainer,
  ModalTitle,
  ModalDescription,
  FormError,
  DynamicFormWrapper,
  SubmitButtonWrapper,
} from 'remote-com-shared/components/ModalForm/styles';
import { BookitScript } from 'remote-com-shared/domains/bookit/Bookit';
import {
  composeBookitPayload,
  formFieldsContainBookit,
} from 'remote-com-shared/domains/bookit/helpers';
import { useBookit } from 'remote-com-shared/domains/bookit/hooks';
import { hasImpactFields } from 'remote-com-shared/domains/impact/helpers';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { LocalisedContentConsumer } from 'remote-com-shared/domains/localisedContent/context';
import { fieldHasContent } from 'remote-com-shared/domains/prismic/helpers';
import { useImpactFormValues } from 'remote-com-shared/domains/shared/trackingParameters';
import { getPartnerStackInfo } from 'remote-com-shared/utils/partnerstack';
import styled, { ThemeProvider } from 'styled-components';
import { v4 as uuid4 } from 'uuid';

import { fields as defaultFields } from './fields';

const { publicRuntimeConfig } = getConfig();

const portalId = publicRuntimeConfig.NEXT_PUBLIC_HUBSPOT_PORTAL_ID;
const contactUsFormId = publicRuntimeConfig.HUBSPOT_CONTACT_US_FORM_ID;

const DEFAULT_CONTACT_US_FORM_ENDPOINT = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${contactUsFormId}/`;

async function sendFormDataAsEmail(formData, formSubmissionsEmailedTo, apiEndpointPath) {
  const valuesFormData = new FormData();

  Object.entries(formData).forEach(([key, value]) => {
    let normalizedValue;

    if (Array.isArray(value)) {
      normalizedValue = JSON.stringify(value);
    } else {
      normalizedValue = value;
    }

    if (normalizedValue) {
      valuesFormData.append(key, normalizedValue);
    }
  });
  valuesFormData.append('formSubmissionsEmailedTo', formSubmissionsEmailedTo);

  await axios.post(`/api/${apiEndpointPath}/`, valuesFormData, {
    headers: {
      enctype: 'multipart/form-data',
    },
  });
}

const ContactUsForm = ({
  onCancel,
  submitURL = DEFAULT_CONTACT_US_FORM_ENDPOINT,
  // allow overriding the default fields for cases like re-ordering
  // the options on a certain landing page
  fields = defaultFields,
  // prefill promo code on paid marketing landing pages
  promoCode,
  /**
   * @type {Array<{option: string; authType: string}>}
   * Options for "reasons" field which should activate schedule memo prompt
   */
  formSubmissionsEmailedTo,
  apiEndpointPath,
}) => {
  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [isCaptchaDone, setIsCaptchaDone] = useState(false);
  const [showSuccessState, setShowSuccessState] = useState(false);

  const { formState } = useFormEvents();
  const [formValues, setFormValues] = useState({ promoCode });

  const shouldLoadBookit = formFieldsContainBookit(fields);
  const {
    submit: submitBookit,
    redirect: redirectBookit,
    ready: bookitIsReady,
    onLoad: initializeBookit,
    scriptConfig,
  } = useBookit();

  const reCaptchaRef = useRef(null);
  const impactFormValues = useImpactFormValues();

  async function handleSubmit(values, { setSubmitting }) {
    const updatedFormValues = {
      ...formValues,
      ...values,
    };
    setFormValues(updatedFormValues);

    setShowLoader(true);
    let paystackPartnerKey = '';

    if (isCaptchaDone) {
      const includeImpactValues = hasImpactFields(values);
      try {
        const publicSlug = uuid4();

        try {
          await createPartnerStackSignup({
            name: `${values.firstName} ${values.lastName}`,
            email: publicSlug,
          });
          paystackPartnerKey = getPartnerKey();
        } catch {
          // Ignore as failing should nor break hubspot
        }

        let payload = {
          firstname: values.firstName,
          lastname: values.lastName,
          company: values.companyName,
          email: values.email,
          location: values.companyHeadquarters,
          reason: values.reason ? values.reason : '',
          target_countries: values.targetCountries
            ? values.targetCountries.map((item) => item.label).join(';')
            : '',
          employees_to_onboard: values.numberOfEmployeesOrContractors,
          employee_count: values.employeeCount,
          promo_code: values.promoCode,
          marketing_content_consent: values.marketingConsent || false,
          pspartnerkey: paystackPartnerKey,
          utm: getCurrentURL(),
          public_slug: publicSlug,
          ...getPartnerStackInfo(),
          ...(includeImpactValues ? impactFormValues : {}),
        };

        let bookitResponse;
        if (bookitIsReady) {
          bookitResponse = await submitBookit(payload);
          payload = composeBookitPayload(payload, bookitResponse);
        }

        await registerLead(payload, submitURL);

        if (formSubmissionsEmailedTo && apiEndpointPath) {
          await sendFormDataAsEmail(values, formSubmissionsEmailedTo, apiEndpointPath);
        }

        const authType = getAuthTypeFromReasonOption(values.reason);
        dataLayerPush({
          event: 'user-submitted-hubspot-form',
          label: window.location.pathname,
          conversion: { send_to: 'AW-594140685/SLovCLa8nOEBEI28p5sC' },
          authType,
        });

        setShowLoader(false);

        if (bookitIsReady && bookitResponse) {
          redirectBookit(bookitResponse);
        }

        setShowSuccessState(true);
      } catch {
        reCaptchaRef.current.reset();
        setIsCaptchaDone(false);
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);
      }
    }
  }

  const handleCaptchaChange = (value) => {
    setIsCaptchaDone(value);
  };

  return (
    <>
      {shouldLoadBookit && <BookitScript onLoad={initializeBookit} scriptConfig={scriptConfig} />}

      <ThemeProvider theme={{ mode: 'light', ...mushuTheme }}>
        <GlobalStyles />

        <FormContainer>
          {showSuccessState ? (
            <SubsribeSuccessContainer>
              <SuccessMark />
              <SuccessTitle>
                <LocalisedContentConsumer>
                  {({ formSuccessThankYou }) => formSuccessThankYou}
                </LocalisedContentConsumer>
              </SuccessTitle>
              <SuccessDescription>
                <LocalisedContentConsumer>
                  {({ contactUsFormSuccessMessage }) => (
                    <p>
                      {fieldHasContent(contactUsFormSuccessMessage)
                        ? asText(contactUsFormSuccessMessage)
                        : 'We’ll contact you to schedule a call, answer any questions you may have, and start onboarding your employees.'}
                    </p>
                  )}
                </LocalisedContentConsumer>
              </SuccessDescription>
              <SuccessActions>
                <SubmitButton onClick={() => onCancel()}>Close</SubmitButton>
              </SuccessActions>
            </SubsribeSuccessContainer>
          ) : (
            <DynamicFormWrapper>
              <LocalisedContentConsumer>
                {({ contactUsFormTitle }) => (
                  <ModalTitle>
                    {fieldHasContent(contactUsFormTitle)
                      ? asText(contactUsFormTitle)
                      : 'Start employing anywhere'}
                  </ModalTitle>
                )}
              </LocalisedContentConsumer>
              <LocalisedContentConsumer>
                {({ contactUsFormDescription }) => (
                  <ModalDescription>
                    {fieldHasContent(contactUsFormDescription)
                      ? asText(contactUsFormDescription)
                      : 'We’ll contact you to schedule a call, answer any questions you may have, and start onboarding your employees.'}
                  </ModalDescription>
                )}
              </LocalisedContentConsumer>
              {showFormError && (
                <LocalisedContentConsumer>
                  {({ formErrorDescription }) => (
                    <FormError>
                      <PrismicRichText
                        field={formErrorDescription}
                        externalLinkComponent={ButtonInline}
                        internalLinkComponent={ButtonInline}
                      />
                    </FormError>
                  )}
                </LocalisedContentConsumer>
              )}

              <DynamicForm
                formName="contact-us-form"
                fields={fields}
                fieldValues={formValues}
                formError={formState.errorMessage}
                formTheme="dark"
                formSize="small"
                handleOnSubmit={handleSubmit}
                isLoading={formState.isLoading}
                enableReinitialize
              />
              <CaptchaWrapper>
                <LocaleContextConsumer>
                  {(locale) => (
                    <ReCAPTCHA
                      hl={locale}
                      ref={reCaptchaRef}
                      sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                      onChange={handleCaptchaChange}
                    />
                  )}
                </LocaleContextConsumer>
              </CaptchaWrapper>
              <SubmitButtonWrapper>
                <SubmitButton
                  isLoading={showLoader}
                  disabled={!isCaptchaDone}
                  form="contact-us-form"
                >
                  <LocalisedContentConsumer>
                    {({ contactUsFormCtaLabel }) => contactUsFormCtaLabel || 'Contact me'}
                  </LocalisedContentConsumer>
                </SubmitButton>
              </SubmitButtonWrapper>
            </DynamicFormWrapper>
          )}
        </FormContainer>
      </ThemeProvider>
    </>
  );
};

const ContactUsFormModal = ({ onCancel, visible, ...props }) => (
  <Modal {...{ visible, onCancel }} width="620px" footer={null} bodyStyle={{ padding: '0px' }}>
    <ContactUsForm {...{ onCancel, ...props }} />
  </Modal>
);

export const OfferIconWrapper = styled.span`
  & > svg {
    fill: var(--colors-turquoise);
    margin-right: 8px;
    height: 20px;
    width: 20px;
    vertical-align: text-bottom;
  }
`;

export const CTA = styled(Button).attrs({
  tone: 'primary',
  shadow: true,
})``;

export const UsualLink = styled.a``;

const ContactUsFormButton = ({ openModal, className, ...props }) => {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (openModal) setShowModal(true);
  }, [openModal]);

  function showDemoModal() {
    setShowModal(true);
  }
  function hideDemoModal() {
    setShowModal(false);
  }

  // Ensures any other props are correctly provided if its a styled component
  const { as: buttonAlias, ...buttonProps } = props.button || {};
  const CTAComponent = buttonAlias || CTA;

  return (
    <>
      <CTAComponent onClick={showDemoModal} className={className} {...(buttonProps || {})}>
        {props.children}
      </CTAComponent>
      <ContactUsFormModal visible={showModal} onCancel={hideDemoModal} {...props} />
    </>
  );
};

const ContactUsFormButtonLink = ({ openModal, className, ...props }) => {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (openModal) setShowModal(true);
  }, [openModal]);

  function showDemoModal() {
    setShowModal(true);
  }
  function hideDemoModal() {
    setShowModal(false);
  }

  return (
    <>
      <UsualLink onClick={showDemoModal} className={className} {...(props.button || {})}>
        {props.children}
      </UsualLink>
      <ContactUsFormModal visible={showModal} onCancel={hideDemoModal} {...props} />
    </>
  );
};

export { ContactUsFormButton, ContactUsFormModal, ContactUsForm, ContactUsFormButtonLink };
